import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const TicketBooking = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingForm, setBookingForm] = useState({
    quantity: 1,
    visitDate: '',
    customerDetails: { name: '', email: '', phone: '' },
    paymentGateway: 'payu'
  });

  useEffect(() => {
    fetchTicketDetails();
    setMinVisitDate();
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/tickets/${ticketId}`, {
        headers: { 'x-tenant-id': 'parks' }
      });
      setTicket(response.data.ticket);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const setMinVisitDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setBookingForm(prev => ({
      ...prev,
      visitDate: tomorrow.toISOString().split('T')[0]
    }));
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    try {
      const bookingData = {
        ticketTypeId: ticket._id,
        ...bookingForm
      };

      const response = await axios.post(`${API_BASE_URL}/api/public/book`, bookingData, {
        headers: { 'x-tenant-id': 'parks' }
      });

      const { payment } = response.data;
      if (payment.paymentUrl) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = payment.paymentUrl;

        Object.keys(payment.params).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = payment.params[key];
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
      }
    } catch (error) {
      alert('Booking failed: ' + (error.response?.data?.message || 'Unknown error'));
    }
  };

  const totalAmount = ticket ? ticket.price * bookingForm.quantity : 0;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎫</div>
          <p style={{ fontSize: '1.2rem', color: '#4a5568' }}>Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>❌</div>
          <h2 style={{ color: '#2d3748', marginBottom: '16px' }}>Ticket Not Found</h2>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'rgba(102, 126, 234, 0.1)',
              color: '#667eea',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '50px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '20px'
            }}
          >
            ← Back to Home
          </button>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#2d3748',
            marginBottom: '8px'
          }}>
            Complete Your Booking
          </h1>
          <p style={{ color: '#718096', fontSize: '1.1rem' }}>
            You're just one step away from an amazing experience!
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
          gap: '40px'
        }}>
          {/* Ticket Details */}
          <div style={{
            background: 'white',
            padding: '32px',
            borderRadius: '24px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            height: 'fit-content'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              marginBottom: '24px'
            }}>
              🎫
            </div>

            <h2 style={{
              margin: '0 0 16px',
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#2d3748'
            }}>
              {ticket.name}
            </h2>

            <div style={{ marginBottom: '24px' }}>
              <span style={{
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                color: 'white',
                padding: '6px 16px',
                borderRadius: '50px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                {ticket.category?.replace('_', ' ')}
              </span>
            </div>

            <p style={{
              color: '#718096',
              fontSize: '1rem',
              lineHeight: '1.6',
              marginBottom: '24px'
            }}>
              {ticket.description || 'Experience the best with our premium service offering unforgettable moments and memories.'}
            </p>

            <div style={{
              display: 'grid',
              gap: '16px',
              padding: '20px',
              background: '#f8fafc',
              borderRadius: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '600', color: '#4a5568' }}>💰 Price per ticket:</span>
                <span style={{ fontWeight: '700', color: '#667eea' }}>₹{ticket.price}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '600', color: '#4a5568' }}>⏰ Validity:</span>
                <span>{ticket.validity?.duration} {ticket.validity?.type}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '600', color: '#4a5568' }}>📍 Location:</span>
                <span>{ticket.city}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '600', color: '#4a5568' }}>✅ Confirmation:</span>
                <span style={{ color: '#48bb78' }}>Instant</span>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <form onSubmit={handleBookingSubmit} style={{
            background: 'white',
            padding: '32px',
            borderRadius: '24px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              margin: '0 0 32px',
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#2d3748'
            }}>
              🎫 Booking Details
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
              gap: '20px',
              marginBottom: '24px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568'
                }}>
                  👥 Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={bookingForm.quantity}
                  onChange={(e) => setBookingForm({...bookingForm, quantity: parseInt(e.target.value)})}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568'
                }}>
                  📅 Visit Date
                </label>
                <input
                  type="date"
                  value={bookingForm.visitDate}
                  onChange={(e) => setBookingForm({...bookingForm, visitDate: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <h4 style={{
              margin: '32px 0 16px',
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#2d3748'
            }}>
              👤 Customer Details
            </h4>

            <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
              <input
                type="text"
                placeholder="Full Name *"
                value={bookingForm.customerDetails.name}
                onChange={(e) => setBookingForm({
                  ...bookingForm,
                  customerDetails: {...bookingForm.customerDetails, name: e.target.value}
                })}
                required
                style={{
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
              <input
                type="email"
                placeholder="Email Address (Optional)"
                value={bookingForm.customerDetails.email}
                onChange={(e) => setBookingForm({
                  ...bookingForm,
                  customerDetails: {...bookingForm.customerDetails, email: e.target.value}
                })}
                style={{
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
              <input
                type="tel"
                placeholder="Phone Number *"
                value={bookingForm.customerDetails.phone}
                onChange={(e) => {
                  const phone = e.target.value.replace(/[^0-9]/g, '');
                  if (phone.length <= 10) {
                    setBookingForm({
                      ...bookingForm,
                      customerDetails: {...bookingForm.customerDetails, phone}
                    });
                  }
                }}
                required
                minLength="10"
                maxLength="10"
                style={{
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#4a5568'
              }}>
                💳 Payment Gateway
              </label>
              <select
                value={bookingForm.paymentGateway}
                onChange={(e) => setBookingForm({...bookingForm, paymentGateway: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '16px',
                  outline: 'none'
                }}
              >
                <option value="payu">💳 PayU</option>
                <option value="icici">🏦 ICICI Bank</option>
                <option value="idfc">🏛️ IDFC Bank</option>
              </select>
            </div>

            <div style={{
              textAlign: 'center',
              padding: '24px',
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              borderRadius: '16px',
              marginBottom: '32px',
              color: 'white'
            }}>
              <div style={{ fontSize: '1.1rem', marginBottom: '8px', opacity: 0.9 }}>
                Total Amount
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '800' }}>
                ₹{totalAmount}
              </div>
            </div>

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '18px',
                background: 'linear-gradient(45deg, #ff6b6b, #feca57)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 8px 30px rgba(255,107,107,0.3)'
              }}
            >
              🚀 Proceed to Payment - ₹{totalAmount}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TicketBooking;